import React, { useMemo, useState } from 'react';
import styled from 'styled-components';
import Button from '@material-ui/core/Button';
import ChevronLeft from '@material-ui/icons/ChevronLeft';
import ChevronRight from '@material-ui/icons/ChevronRight';
import { colors } from '../../config/theme';

const CalendarShell = styled.div`
  width: 100%;
  margin: 16px 0;
  background: #fff;
  border-radius: 8px;
  padding: 12px;
  box-sizing: border-box;

  @media (max-width: 600px) {
    width: calc(100% + 32px);
    margin: 16px -16px;
    border-radius: 0;
    padding: 12px 16px;
  }
`;

const CalendarHeader = styled.div`
  display: flex;
  align-items: center;
  justify-content: space-between;
  margin-bottom: 12px;
`;

const Weekdays = styled.div`
  display: grid;
  grid-template-columns: repeat(7, minmax(0, 1fr));
  gap: 2px;
  color: ${colors.textSecondary};
  font-size: 12px;
  text-align: center;
`;

const DayGrid = styled.div`
  display: grid;
  grid-template-columns: repeat(7, minmax(0, 1fr));
  gap: 2px;
`;

const DayButton = styled.button`
  appearance: none;
  position: relative;
  border: 0;
  border-radius: 50%;
  background: ${({ selected }) => (selected ? colors.primary : 'transparent')};
  color: ${({ selected }) => (selected ? colors.onPrimary : colors.textPrimary)};
  font: inherit;
  width: 42px;
  height: 42px;
  min-width: 42px;
  justify-self: center;
  cursor: ${({ disabled }) => (disabled ? 'default' : 'pointer')};
  opacity: ${({ outside }) => (outside ? 0 : 1)};
  pointer-events: ${({ outside }) => (outside ? 'none' : 'auto')};

  &:focus { outline: 2px solid ${colors.primary}; outline-offset: 2px; }
  &:hover { background: ${({ disabled, selected }) => (!disabled && !selected ? colors.surfaceRaised : undefined)}; }

  ${({ available, selected }) => available && !selected && `
    &::after {
      content: '';
      position: absolute;
      width: 6px;
      height: 6px;
      border-radius: 50%;
      background: #35a854;
      bottom: 4px;
      left: calc(50% - 3px);
    }
  `}

  ${({ unavailable, selected }) => unavailable && !selected && `
    color: ${colors.textDisabled};
    text-decoration: line-through;
  `}
`;

const dateKey = (date) => {
  const month = `${date.getMonth() + 1}`.padStart(2, '0');
  const day = `${date.getDate()}`.padStart(2, '0');
  return `${date.getFullYear()}-${month}-${day}`;
};
const firstOfMonth = (date) => new Date(date.getFullYear(), date.getMonth(), 1);
const monthLabel = (date) => date.toLocaleDateString([], { month: 'long', year: 'numeric' });

const AvailabilityCalendar = ({ availableDays, selectedDay, onSelect, minDate, maxDate }) => {
  const [visibleMonth, setVisibleMonth] = useState(firstOfMonth(new Date()));
  const min = minDate ? new Date(`${minDate}T00:00:00`) : null;
  const max = maxDate ? new Date(`${maxDate}T23:59:59`) : null;
  const cells = useMemo(() => {
    const first = firstOfMonth(visibleMonth);
    const start = new Date(first);
    start.setDate(first.getDate() - first.getDay());
    return Array.from({ length: 42 }, (_, index) => {
      const date = new Date(start);
      date.setDate(start.getDate() + index);
      return date;
    });
  }, [visibleMonth]);
  const canGoPrevious = !min || firstOfMonth(visibleMonth) > firstOfMonth(min);
  const canGoNext = !max || firstOfMonth(visibleMonth) < firstOfMonth(max);

  return (
    <CalendarShell aria-label="Coach availability calendar">
      <CalendarHeader>
        <Button aria-label="Previous month" disabled={!canGoPrevious} onClick={() => setVisibleMonth(new Date(visibleMonth.getFullYear(), visibleMonth.getMonth() - 1, 1))}><ChevronLeft /></Button>
        <strong>{monthLabel(visibleMonth)}</strong>
        <Button aria-label="Next month" disabled={!canGoNext} onClick={() => setVisibleMonth(new Date(visibleMonth.getFullYear(), visibleMonth.getMonth() + 1, 1))}><ChevronRight /></Button>
      </CalendarHeader>
      <Weekdays>{['Sun', 'Mon', 'Tue', 'Wed', 'Thu', 'Fri', 'Sat'].map((day) => <span key={day}>{day}</span>)}</Weekdays>
      <DayGrid>
        {cells.map((date) => {
          const key = dateKey(date);
          const outside = date.getMonth() !== visibleMonth.getMonth();
          const available = availableDays.has(key);
          const outsideRange = (min && date < min) || (max && date > max);
          const disabled = outside || outsideRange || !available;
          return <DayButton key={key} type="button" outside={outside} available={available} unavailable={!available && !outsideRange} disabled={disabled} selected={selectedDay === key} aria-label={`${key}${available ? ', available' : ', unavailable'}`} onClick={() => !disabled && onSelect(key)}>{date.getDate()}</DayButton>;
        })}
      </DayGrid>
    </CalendarShell>
  );
};

export default AvailabilityCalendar;
